import { isAfter, isBefore } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppAuth } from '~/hooks/appAuth';
import { useCart } from '~/hooks/cart';
import { useCoupons } from '~/hooks/coupons';
import { useSettings } from '~/hooks/settings';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

type CouponProps = {
  orderValue: number;
  onOpenAuthModal: () => void;
};

export function Coupon(props: CouponProps) {
  const [show, setShow] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [discount, setDiscount] = useState(0);

  const { user } = useAppAuth();
  const { company } = useSettings();
  const { loadCoupons, coupons, findUsedCoupons } = useCoupons();
  const { coupon, setCoupon } = useCart();

  useEffect(() => {
    if (coupons.length === 0 && company.uid) {
      loadCoupons(company.uid);
    }
  }, [coupons, company, loadCoupons]);

  const handleSubmit = useCallback(async () => {
    if (!user || !user.uid) {
      props.onOpenAuthModal();
      return;
    }

    setError('');

    const findCoupon = coupons.find(item => item.code === code);

    if (!findCoupon || !findCoupon.status) {
      setError('Código de cupom inválido.');
      return;
    }

    if (props.orderValue < findCoupon.min_value) {
      setError(
        `Para utilização do Cupom o seu pedido mínimo deve ser no valor de ${getCurrencyFormatted(
          findCoupon.min_value,
        )}.`,
      );
      return;
    }

    const currentDate = new Date();
    const initDate = new Date(findCoupon.initDate);
    const endDate = new Date(findCoupon.endDate);

    const isAfterInit = isAfter(currentDate, initDate);
    const isBeforeEnd = isBefore(currentDate, endDate);

    if (!isAfterInit || !isBeforeEnd) {
      setError('Cupom não está disponível.');
      return;
    }

    if (findCoupon.phone_limit) {
      const usedCoupons = await findUsedCoupons({
        company_id: company.uid,
        coupon_id: findCoupon.uid,
        phone: user.whatsapp,
      });

      if (usedCoupons.length > 0) {
        setError(
          'Você já utilizou esse cupom. Digite um novo cupom e tente novamente.',
        );
        return;
      }
    }

    const discountValue =
      findCoupon.type === 0
        ? parseFloat(((props.orderValue * findCoupon.value) / 100).toFixed(2))
        : findCoupon.value;

    setDiscount(discountValue);
    setCoupon(findCoupon);
    setShow(false);
  }, [code, coupons, props, setCoupon, user, findUsedCoupons, company]);

  return (
    <>
      <div className="row mb-2">
        <div className="col" style={{ display: 'flex', alignItems: 'center' }}>
          <p>Cupom de desconto</p>
        </div>
        <div className="col-auto pr-0">
          <button
            type="button"
            className="btn btn-light text-capitalize"
            style={{ fontSize: 12 }}
            onClick={() => {
              if (!user || !user.uid) {
                props.onOpenAuthModal();
                return;
              }

              setShow(true);
            }}
          >
            {coupon.uid ? coupon.code : 'Informar Código'}
          </button>
        </div>
      </div>

      {coupon.uid && (
        <div className="row mb-2">
          <div
            className="col"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <p>Valor do desconto</p>
          </div>
          <div className="col-auto pr-0">{getCurrencyFormatted(discount)}</div>
        </div>
      )}

      {show && (
        <div
          className="modal fade show"
          id="addproductcart"
          tabIndex={-1}
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content product border-0 shadow-sm">
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <h5 className="mb-2">Informe o Código do Cupom</h5>

                    <form className="mt-3">
                      <div className="form-floating mb-2 border rounded">
                        <input
                          type="text"
                          className="form-control"
                          id="reference"
                          placeholder="Informe o código do cupom de desconto"
                          onChange={event => {
                            setCode(event.target.value);
                          }}
                        />
                        <label htmlFor="select">Código</label>
                      </div>
                      {error && <p className="invalid">{error}</p>}
                    </form>
                  </div>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="btn btn-link text-color-theme"
                  onClick={handleSubmit}
                  style={{ color: company.app_color }}
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShow(false)}
            style={{ background: '#00000060' }}
          />
        </div>
      )}
    </>
  );
}
